import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  RefreshCw, Send, AlertTriangle, Clock, User, Scissors,
  DollarSign, Phone, Mail, CalendarClock, MessageSquare, Check, X
} from "lucide-react";

const urgencyStyles = {
  critical: { bg: "bg-[#FDE8E8]", text: "text-rose-600", border: "border-[#F5C6C6]", label: "Critical" },
  overdue: { bg: "bg-[#FEF3CD]", text: "text-[#856404]", border: "border-[#FCEABB]", label: "Overdue" },
  upcoming: { bg: "bg-[#D4EDDA]", text: "text-[#155724]", border: "border-[#C3E6CB]", label: "Due Soon" },
};

export default function RebookPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState("all");
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [msgTemplate, setMsgTemplate] = useState("Hi {name}, it's been a while! Your usual {services} appointment is overdue. Book now to keep your look fresh!");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dueRes, logRes] = await Promise.all([
        api.get("/rebook/due-clients"),
        api.get("/rebook/log?limit=20"),
      ]);
      setData(dueRes.data);
      setLog(logRes.data || []);
    } catch { toast.error("Failed to load rebook data"); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    const ids = filteredClients.map(c => c.client_id);
    setSelected(prev => prev.size === ids.length ? new Set() : new Set(ids));
  };

  const sendReminders = async () => {
    if (selected.size === 0) { toast.error("Select clients first"); return; }
    setSending(true);
    try {
      const res = await api.post("/rebook/send-reminders", {
        client_ids: Array.from(selected),
        message: msgTemplate,
        channel: "sms",
      });
      toast.success(`Sent ${res.data.sent_count} reminders!`);
      setSelected(new Set());
      fetchData();
    } catch { toast.error("Failed to send"); }
    setSending(false);
  };

  const clients = data?.clients || [];
  const summary = data?.summary || {};
  const filteredClients = filter === "all" ? clients : clients.filter(c => c.urgency === filter);

  if (loading) return <div className="p-8 flex items-center justify-center min-h-[400px]"><RefreshCw className="w-5 h-5 animate-spin text-rose-400" /></div>;

  return (
    <div data-testid="rebook-page" className="p-6 lg:p-8 max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">Due for Rebook</h1>
          <p className="text-sm text-slate-400 mt-1">Smart automated campaigns for overdue clients</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowLog(!showLog)} className="border-slate-200 text-slate-600" data-testid="toggle-log-btn">
            <MessageSquare className="w-4 h-4 mr-1.5" /> {showLog ? "Hide" : "Show"} Log ({log.length})
          </Button>
          <Button size="sm" onClick={sendReminders} disabled={sending || selected.size === 0} className="bg-slate-900 text-white" data-testid="send-reminders-btn">
            <Send className="w-4 h-4 mr-1.5" /> {sending ? "Sending..." : `Send (${selected.size})`}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div data-testid="stat-total-due" className="bg-white border border-slate-200 rounded-2xl p-5">
          <CalendarClock className="w-5 h-5 text-[#8B5CF6] mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900">{summary.total_due || 0}</p>
          <p className="text-xs text-slate-400">Total Due</p>
        </div>
        <div data-testid="stat-critical" className="bg-white border border-slate-200 rounded-2xl p-5">
          <AlertTriangle className="w-5 h-5 text-rose-600 mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-rose-600">{summary.critical || 0}</p>
          <p className="text-xs text-slate-400">Critical</p>
        </div>
        <div data-testid="stat-overdue" className="bg-white border border-slate-200 rounded-2xl p-5">
          <Clock className="w-5 h-5 text-[#856404] mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-[#856404]">{summary.overdue || 0}</p>
          <p className="text-xs text-slate-400">Overdue</p>
        </div>
        <div data-testid="stat-upcoming" className="bg-white border border-slate-200 rounded-2xl p-5">
          <Check className="w-5 h-5 text-[#155724] mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-[#155724]">{summary.upcoming || 0}</p>
          <p className="text-xs text-slate-400">Due Soon</p>
        </div>
      </div>

      {/* Message Template */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4">
        <label className="text-xs text-slate-400 mb-1 block">Message Template (use {"{name}"} and {"{services}"})</label>
        <Input value={msgTemplate} onChange={e => setMsgTemplate(e.target.value)} className="border-slate-200 text-sm" data-testid="msg-template" />
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: "all", label: "All Due", count: summary.total_due },
          { key: "critical", label: "Critical", count: summary.critical },
          { key: "overdue", label: "Overdue", count: summary.overdue },
          { key: "upcoming", label: "Due Soon", count: summary.upcoming },
        ].map(tab => (
          <button key={tab.key} onClick={() => setFilter(tab.key)} data-testid={`filter-${tab.key}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${filter === tab.key ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-rose-300"}`}>
            {tab.label} ({tab.count || 0})
          </button>
        ))}
      </div>

      {/* Client Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 bg-[#FAFAF9]">
              <th className="py-3 px-4 text-left">
                <input type="checkbox" checked={selected.size === filteredClients.length && filteredClients.length > 0} onChange={selectAll} className="rounded border-slate-200" data-testid="select-all" />
              </th>
              <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Client</th>
              <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Urgency</th>
              <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Days Overdue</th>
              <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Avg Gap</th>
              <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Usual Services</th>
              <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Provider</th>
              <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">LTV</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-slate-400">
                <Check className="w-8 h-8 mx-auto mb-2 text-[#D1D1D1]" />
                <p>No clients due for rebook</p>
              </td></tr>
            ) : filteredClients.map(c => {
              const u = urgencyStyles[c.urgency] || urgencyStyles.upcoming;
              return (
                <tr key={c.client_id} data-testid={`rebook-row-${c.client_id}`} className="border-b border-slate-100 hover:bg-[#FAFAF9]">
                  <td className="py-3 px-4">
                    <input type="checkbox" checked={selected.has(c.client_id)} onChange={() => toggleSelect(c.client_id)} className="rounded border-slate-200" />
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-sm font-medium text-slate-900">{c.client_name}</p>
                    <p className="text-xs text-slate-400">{c.phone}</p>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${u.bg} ${u.text} ${u.border}`}>{u.label}</span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className="font-semibold text-slate-900">{c.overdue_days}</span>
                    <span className="text-xs text-slate-400 ml-0.5">days</span>
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-slate-600">{c.avg_visit_gap_days}d</td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-1">{c.usual_services?.map((s, i) => <span key={i} className="text-xs bg-[#FAFAFA] px-2 py-0.5 rounded border border-slate-200 text-slate-600">{s}</span>)}</div>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600">{c.preferred_provider || "—"}</td>
                  <td className="py-3 px-4 text-center text-sm font-semibold text-emerald-600">${c.lifetime_value?.toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Send Log */}
      {showLog && log.length > 0 && (
        <div data-testid="rebook-log" className="bg-white border border-slate-200 rounded-2xl p-5">
          <h3 className="font-['Outfit'] font-semibold text-slate-900 mb-3">Recent Reminders Sent</h3>
          <div className="space-y-2">
            {log.map((l, i) => (
              <div key={i} className="flex items-center gap-3 p-2 rounded border border-slate-100 text-xs">
                <Send className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                <span className="font-medium text-slate-900">{l.client_name}</span>
                <span className="text-slate-400 flex-1 truncate">{l.message}</span>
                <span className="text-slate-400 flex-shrink-0">{new Date(l.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
